import type { AnglesResult, HousesResult, HouseSystem } from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import {
  asinDeg,
  atan2Deg,
  cosDeg,
  inArc,
  norm360,
  sinDeg,
  tanDeg,
} from '../math/angles.js';

/**
 * Angles and house systems.
 *
 * RAMC = local apparent sidereal time expressed in degrees.
 * Formulas per Meeus / standard astrological practice:
 *   MC  = atan2( sin θ, cos θ · cos ε )
 *   Asc = atan2( cos θ, −( sin θ · cos ε + tan φ · sin ε ) )
 * Placidus cusps 11,12,2,3 are found by the classical semi-arc iteration;
 * remaining cusps are the opposites of 10,11,12,1,2,3.
 */

export class PlacidusUndefinedError extends Error {
  constructor(latitude: number) {
    super(
      `Placidus houses are undefined at latitude ${latitude.toFixed(2)}° for this chart ` +
        `(circumpolar ecliptic degrees). Falling back to whole-sign.`,
    );
    this.name = 'PlacidusUndefinedError';
  }
}

export function localSiderealDegrees(
  utc: Date,
  longitudeEast: number,
  adapter: EphemerisAdapter,
): number {
  const gastHours = adapter.siderealTimeHours(utc);
  return norm360(gastHours * 15 + longitudeEast);
}

export function computeAngles(
  utc: Date,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  ayanamsaDeg: number,
): AnglesResult {
  const theta = localSiderealDegrees(utc, longitudeEast, adapter); // RAMC
  const eps = adapter.obliquity(utc);

  const mc = atan2Deg(sinDeg(theta), cosDeg(theta) * cosDeg(eps));
  const asc = atan2Deg(
    cosDeg(theta),
    -(sinDeg(theta) * cosDeg(eps) + tanDeg(latitude) * sinDeg(eps)),
  );

  return {
    ascendant: asc,
    mc,
    ascendantSidereal: norm360(asc - ayanamsaDeg),
    mcSidereal: norm360(mc - ayanamsaDeg),
  };
}

/** Ecliptic longitude whose right ascension is `ra`. */
function eclipticOfRa(ra: number, eps: number): number {
  return atan2Deg(sinDeg(ra), cosDeg(ra) * cosDeg(eps));
}

function declinationOfEcliptic(lambda: number, eps: number): number {
  return asinDeg(sinDeg(eps) * sinDeg(lambda));
}

/**
 * Solve one intermediate Placidus cusp.
 * @param targetRaBase RAMC-relative base right ascension (degrees)
 * @param fraction     fraction of the semi-arc (1/3 or 2/3)
 * @param diurnal      true for cusps 11/12 (diurnal semi-arc), false for 2/3
 */
function placidusCusp(
  ramc: number,
  eps: number,
  latitude: number,
  fraction: number,
  diurnal: boolean,
): number {
  // Initial guess: equator case (AD = 0).
  const baseRa = diurnal ? ramc + fraction * 90 : ramc + 180 - fraction * 90;
  let lambda = eclipticOfRa(norm360(baseRa), eps);

  for (let i = 0; i < 30; i++) {
    const dec = declinationOfEcliptic(lambda, eps);
    const x = tanDeg(latitude) * tanDeg(dec);
    if (Math.abs(x) >= 1) throw new PlacidusUndefinedError(latitude);
    const ad = asinDeg(x); // ascensional difference
    const targetRa = diurnal
      ? norm360(ramc + fraction * (90 + ad))
      : norm360(ramc + 180 - fraction * (90 - ad));
    const next = eclipticOfRa(targetRa, eps);
    if (Math.abs(norm360(next - lambda + 180) - 180) < 1e-7) {
      lambda = next;
      break;
    }
    lambda = next;
  }
  return norm360(lambda);
}

export function placidusHouses(
  utc: Date,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  angles: { ascendant: number; mc: number },
): HousesResult {
  const ramc = localSiderealDegrees(utc, longitudeEast, adapter);
  const eps = adapter.obliquity(utc);

  const c11 = placidusCusp(ramc, eps, latitude, 1 / 3, true);
  const c12 = placidusCusp(ramc, eps, latitude, 2 / 3, true);
  const c2 = placidusCusp(ramc, eps, latitude, 2 / 3, false);
  const c3 = placidusCusp(ramc, eps, latitude, 1 / 3, false);

  const cusps = new Array<number>(12);
  cusps[0] = angles.ascendant; // 1st
  cusps[1] = c2;
  cusps[2] = c3;
  cusps[3] = norm360(angles.mc + 180); // IC
  cusps[4] = norm360(c11 + 180);
  cusps[5] = norm360(c12 + 180);
  cusps[6] = norm360(angles.ascendant + 180); // descendant
  cusps[7] = norm360(c2 + 180);
  cusps[8] = norm360(c3 + 180);
  cusps[9] = angles.mc; // 10th
  cusps[10] = c11;
  cusps[11] = c12;

  return { system: 'placidus', cusps };
}

/** Whole-sign houses from the (tropical) ascendant: cusps at sign starts. */
export function wholeSignHouses(ascendant: number): HousesResult {
  const startSign = Math.floor(norm360(ascendant) / 30);
  const cusps = Array.from({ length: 12 }, (_, i) => norm360((startSign + i) * 30));
  return { system: 'whole-sign', cusps };
}

/**
 * Equal houses: 30° segments from the exact Ascendant degree. Common in
 * traditional and Indian-influenced Western practice, and well-defined at
 * every latitude (unlike Placidus).
 */
export function equalHouses(ascendant: number): HousesResult {
  const cusps = Array.from({ length: 12 }, (_, i) => norm360(ascendant + i * 30));
  return { system: 'equal', cusps };
}

export function housesFor(
  system: HouseSystem,
  utc: Date,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  angles: { ascendant: number; mc: number },
): HousesResult {
  if (system === 'whole-sign') return wholeSignHouses(angles.ascendant);
  if (system === 'equal') return equalHouses(angles.ascendant);
  try {
    return placidusHouses(utc, latitude, longitudeEast, adapter, angles);
  } catch (e) {
    if (e instanceof PlacidusUndefinedError) {
      return { ...wholeSignHouses(angles.ascendant), fallbackApplied: true };
    }
    throw e;
  }
}

/** House index (1-12) of a longitude given ordered cusps. */
export function houseOf(lon: number, cusps: number[]): number {
  for (let i = 0; i < 12; i++) {
    const a = cusps[i];
    const b = cusps[(i + 1) % 12];
    if (inArc(norm360(lon), a, b)) return i + 1;
  }
  return 12; // numerically exact boundary edge case
}
